import { Button, Flex, type FlexProps, Grid, Icon, Image, Text } from "@chakra-ui/react";
import { ArrowUpRightIcon, ShieldedIcon, TokenIcon } from "@icons";
import {
  type ChainFullId,
  ChainFullIds,
  ChainIds,
  formatCurrency,
  formatTokenBalance,
  getTokenSymbol,
  isMultichainToken,
  tokenNameSafe,
  tokenPrettyAmount,
} from "@umami/chains";
import { useDynamicModalContext } from "@umami/components";
import { type Provider } from "@umami/ramp";
import {
  type UnifiedTokenWithPrices,
  useAppSelector,
  useCurrentAccount,
  useGetChainBalance,
  useGetNetworkChain,
  useGetNetworkSymbol,
  useGetSaplingAccount,
} from "@umami/state";

import { useColor } from "../../styles/useColor";
import { FormPage as BuyTokenFormPage, type QuoteParams } from "../Buy/FormPage";
import { BuyProviderModal } from "../BuyProviderModal/BuyProviderModal";
import { IconButtonWithText } from "../IconButtonWithText/IconButtonWithText";
import { TokenIconWrapper } from "../IconWrapper/TokenIconWrapper";
import { ProviderSelectModal } from "../ProviderSelectModal";
import { ActivateSaplingModal } from "../Sappling/ActivateSaplingModal";
import { PrepareTransactionPage as SendShieldedFormPage } from "../Sappling/SendShieldedFlow";
import { FormPage as ShieldFormModal } from "../Sappling/ShieldFlow";
import { InsufficientFunds } from "../SendFlow/InsufficientFunds";
import { FormPage as SendTezFormPage } from "../SendFlow/Tez/FormPage";
import { FormPage as SendTokenFormPage } from "../SendFlow/Token/FormPage";
import { Tag } from "../Tag/Tag";

import { ChainIcon } from "@/components/Token/ChainIcon";

type TokenProps = (
  | {
      token: UnifiedTokenWithPrices;
      balance?: never;
      usdBalance?: never;
      showBalances?: boolean;
      name?: never;
      chain?: ChainFullId;
      hasSendButton?: boolean;
      hasBuyButton?: boolean;
      hasSellButton?: boolean;
      hasShieldButton?: boolean;
      hasUnshieldButton?: boolean;
      usdPrice?: never;
      iconUrl?: never;
      isShieldedBalance?: boolean;
    }
  | {
      balance: number;
      usdPrice: number;
      usdBalance: number;
      showBalances?: boolean;
      name: string;
      chain?: ChainFullId;
      hasSendButton?: boolean;
      hasBuyButton?: boolean;
      hasSellButton?: boolean;
      hasShieldButton?: boolean;
      hasUnshieldButton?: boolean;
      token?: never;
      iconUrl?: string;
      isShieldedBalance?: boolean;
    }
) &
  FlexProps;

const processTezosName = (name: string) => {
  if (name === "mainnet") {
    return "Tezos";
  } else if (name === "shadownet") {
    return "Tezos Shadownet";
  } else if (name === "michelson") {
    return "Tezos Michelson";
  } else if (name === "michelson shadownet") {
    return "Tezos Michelson Shadownet";
  } else if (name === "previewnet") {
    return "Tezos Previewnet";
  }

  return name;
};

export const TokenMultichain = ({
  name,
  usdBalance,
  usdPrice,
  balance,
  showBalances = true,
  chain = ChainFullIds.TEZOS,
  token,
  hasSendButton,
  hasBuyButton,
  hasSellButton,
  hasShieldButton,
  hasUnshieldButton,
  iconUrl,
  isShieldedBalance = false,
  ...props
}: TokenProps) => {
  const color = useColor();
  const { configs } = useAppSelector(state => state.networks);
  const { openWith } = useDynamicModalContext();
  const currentAccount = useCurrentAccount()!;
  const accountBalance = Number(useGetChainBalance()(token?.chainId ?? chain).balance);
  const getNetworkChain = useGetNetworkChain();
  const getNetworkSymbol = useGetNetworkSymbol();

  const saplingAccount = useGetSaplingAccount(currentAccount.address.pkh);
  const isSaplingActive = !!saplingAccount;

  const handleSend = async () => {
    if (isShieldedBalance) {
      if (!isSaplingActive) {
        await openWith(<ActivateSaplingModal />);

        return;
      }

      await openWith(<SendShieldedFormPage chainId={chain} />);

      return;
    }

    if (!accountBalance) {
      return await openWith(<InsufficientFunds chainId={token?.chainId ?? chain} />);
    }

    if (token) {
      const chainFullId = isMultichainToken(token) ? token.chainId : chain;
      await openWith(
        <SendTokenFormPage chainId={chainFullId} sender={currentAccount} token={token} />
      );
    } else {
      await openWith(<SendTezFormPage chainId={chain} sender={currentAccount} />);
    }
  };

  const handleBuy = async () => {
    await openWith(
      <BuyTokenFormPage
        fiatCurrency="USD"
        iconUrl={token && isMultichainToken(token) ? token.iconUrl : iconUrl}
        name={getTokenName()}
        network={token?.chainId ?? chain}
        onBuyClick={handleBuyClick}
        onProviderSelect={handleProviderSelect}
        sender={currentAccount}
        tokenSymbol={token ? (getTokenSymbol(token) ?? "") : getNetworkSymbol(chain)}
      />
    );
  };

  const handleProviderSelect = (amount: string, providers: Provider[], quoteParams: QuoteParams) =>
    openWith(
      <ProviderSelectModal amount={amount} providers={providers} quoteParams={quoteParams} />
    );

  const handleBuyClick = async (apiUrl: string, openInNewTab: boolean) => {
    await openWith(<BuyProviderModal apiUrl={apiUrl} openInNewTab={openInNewTab} />);
  };

  const handleShield = async () => {
    if (!isSaplingActive) {
      await openWith(<ActivateSaplingModal />);

      return;
    }
    await openWith(<ShieldFormModal chainId={chain} />);
  };

  const handleUnshield = async () => {
    if (!isSaplingActive) {
      await openWith(<ActivateSaplingModal />);

      return;
    }
    await openWith(<SendShieldedFormPage chainId={chain} isUnshield />);
  };

  const getTokenName = () => {
    if (!token) {
      return name;
    }

    if (isMultichainToken(token)) {
      return token.name;
    }

    return tokenNameSafe(token);
  };

  const getTokenBalance = () => {
    if (!token) {
      return formatTokenBalance(balance);
    }

    if (isMultichainToken(token)) {
      return token.balance;
    }

    return tokenPrettyAmount(token.balance, token, { showSymbol: false });
  };

  const getTokenChain = () => {
    let chainConfig;

    if (token && isMultichainToken(token)) {
      chainConfig = configs[token.chainId];
    } else {
      chainConfig = configs[chain];
    }

    if (chainConfig.chain === ChainIds.TEZOS) {
      return processTezosName(chainConfig.name);
    }

    return chainConfig.name;
  };

  const hasActionButtons = hasSendButton || hasBuyButton || hasSellButton || hasShieldButton;

  return (
    <Grid
      as="li"
      gridRowGap={{ base: "18px", md: "0" }}
      gridTemplateColumns={{
        base: "auto",
        md: hasActionButtons ? "1fr 0.5fr 1fr" : "auto 1fr",
      }}
      gridTemplateAreas={{
        base: hasActionButtons ? '"token token" "amount action"' : '"token amount"',
        md: hasActionButtons ? '"token amount action"' : '"token amount"',
      }}
      width="full"
      _hover={
        hasActionButtons
          ? undefined
          : {
              background: color("100") + "80",
              borderRadius: "12px",
              cursor: "pointer",
            }
      }
      data-testid="multichain-token"
      {...props}
    >
      <Flex alignItems="center" flex="1" gridArea="token">
        <Flex alignItems="center" flex="0 0 auto">
          {token ? (
            <TokenIconWrapper>
              <TokenIcon width="48px" minWidth="48px" rounded="full" token={token} />
            </TokenIconWrapper>
          ) : iconUrl ? (
            <TokenIconWrapper>
              <Image
                width="48px"
                minWidth="48px"
                fallbackSrc="./static/coin-front.svg"
                rounded="full"
                src={iconUrl}
              />
            </TokenIconWrapper>
          ) : (
            <ChainIcon boxSize="48px" chainId={getNetworkChain(chain)} />
          )}
        </Flex>
        <Flex flexDirection="column" flex="1" gap="4px" maxWidth="304px" marginLeft="12px">
          <Flex alignItems="center" gap="6px">
            {isShieldedBalance && (
              <Icon as={ShieldedIcon} width="20px" height="20px" color={color("600")} />
            )}
            <Text
              overflow="hidden"
              maxWidth="140px"
              color={color("black")}
              fontWeight="600"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
              data-testid="token-symbol"
              size="md"
            >
              {getTokenName()}
            </Text>
            <Tag size="sm" variant="square">
              {getTokenChain()}
            </Tag>
          </Flex>
          {showBalances && (
            <Flex alignItems="center" gap="8px">
              <Text color={color("400")} data-testid="token-price" size="sm">
                {formatCurrency(usdPrice ?? token.usdPrice)}
              </Text>
            </Flex>
          )}
        </Flex>
      </Flex>
      {showBalances && (
        <Flex
          flex="1"
          justifySelf={hasActionButtons ? "auto" : "end"}
          alignSelf="center"
          gap="4px"
          gridArea="amount"
        >
          <Flex
            alignItems={hasActionButtons ? "flex-start" : "flex-end"}
            flexDirection="column"
            flex={hasActionButtons ? "1" : "0 0 auto"}
            gap="4px"
          >
            <Text color={color("black")} fontWeight="600" data-testid="token-balance" size="md">
              {getTokenBalance()}
            </Text>
            <Text color={color("400")} data-testid="token-usd-balance" size="sm">
              {formatCurrency(usdBalance ?? token.totalUsdPrice)}
            </Text>
          </Flex>
        </Flex>
      )}
      {hasActionButtons && (
        <Flex alignItems="center" justifySelf="end" gap="10px" gridArea="action" marginLeft="16px">
          {hasBuyButton && (
            <Button
              width={["100%", "100%", "100%", "120px"]}
              padding="10px 24px"
              onClick={handleBuy}
              size="lg"
              variant="iconButtonSolid"
            >
              Buy
            </Button>
          )}
          {hasSellButton && (
            <Button
              width={["100%", "100%", "100%", "120px"]}
              padding="10px 24px"
              onClick={handleBuy}
              size="lg"
              variant="iconButtonSolid"
            >
              Sell
            </Button>
          )}
          {hasShieldButton && (
            <Button
              width={["100%", "100%", "100%", "120px"]}
              padding="10px 24px"
              onClick={handleShield}
              size="lg"
              variant="iconButtonSolid"
            >
              Shield
            </Button>
          )}
          {hasUnshieldButton && (
            <Button
              width={["100%", "100%", "100%", "120px"]}
              padding="10px 24px"
              onClick={handleUnshield}
              size="lg"
              variant="iconButtonSolid"
            >
              Unshield
            </Button>
          )}
          {hasSendButton && (
            <IconButtonWithText
              icon={ArrowUpRightIcon}
              label="Send"
              onClick={handleSend}
              showLabel={false}
              variant="primary"
            />
          )}
        </Flex>
      )}
    </Grid>
  );
};
